import { type MutableRefObject, type ReactNode, useState } from 'react';
import { MAX_IMAGE_RATIO } from '@/app/constants';
import type { ImageRange } from '@/app/types';

type Move = 'left' | 'top' | 'right' | 'bottom' | 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
const MAX_DETECT_RANGE = 60; // 图片选择框的最大边框宽度
const MIN_DETECT_RANGE = 20; // 图片选择框的最小边框宽度
const MIN_CENTER_SIZE = 40; // 中心区域的最小尺寸
const lineStyle = {
  strokeWidth: 2, // 线条宽度
  stroke: 'currentColor', // 线条颜色
  strokeDasharray: '5 5', // 线条虚线
};
const MIN_CROP_LENGTH = 50;

const isValidPosition = (tempPosition: ImageRange, imageScale: number, maxCropAspectRatio: number) => {
  const [left, top, right, bottom] = tempPosition;
  if (left >= right || top >= bottom) return false; // 检查坐标合理性
  const width = right - left;
  const height = bottom - top;
  if (width < imageScale * MIN_CROP_LENGTH || height < imageScale * MIN_CROP_LENGTH) return false; // 检查最小边长
  if (width / height > maxCropAspectRatio || height / width > maxCropAspectRatio) return false; // 检查宽高比限制
  return true;
};

type ClipMaskParams = {
  range: ImageRange;
  selectedAspect: number | 'free';
  maxImageRange: MutableRefObject<ImageRange>;
  imageScale: number; //图片缩放比例
  setRange: (range: ImageRange) => void;
};
type ClipMaskResult = {
  handleMoving: (event: React.PointerEvent) => void;
  handleMoveEnd: () => void;
  clipMaskDom: ReactNode;
};
export default function useClipMask(props: ClipMaskParams): ClipMaskResult {
  const { range, selectedAspect, maxImageRange, imageScale, setRange } = props;
  const [moveStart, setMoveStart] = useState<[Move, number, number] | null>(null); // 鼠标移动的起点

  // 动态计算感应区大小
  const [rangeLeft, rangeTop, rangeRight, rangeBottom] = range;
  const cropWidth = rangeRight - rangeLeft;
  const cropHeight = rangeBottom - rangeTop;
  const minSize = Math.min(cropWidth, cropHeight);

  // 根据裁剪框大小动态调整感应区大小
  // 确保中心区域至少有 MIN_CENTER_SIZE 的空间
  const maxAllowedDetectRange = Math.max(MIN_DETECT_RANGE, (minSize - MIN_CENTER_SIZE) / 2);
  const DETECT_RANGE = Math.min(MAX_DETECT_RANGE, maxAllowedDetectRange);
  const handleMoving = (event: React.PointerEvent) => {
    if (!moveStart) return;
    const { offsetX, offsetY } = event.nativeEvent;

    const dx = offsetX - moveStart[1];
    const dy = offsetY - moveStart[2];
    const tempPosition = [...range] as ImageRange;
    const aspect = selectedAspect !== 'free' ? Number(selectedAspect) : null;

    switch (moveStart[0]) {
      case 'left':
        if (aspect) {
          // 固定比例，左边跟随鼠标，固定右边，上下两边同时增加
          let newLeft = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
          let width = tempPosition[2] - newLeft;
          let height = width / aspect;

          // 计算上下居中的位置
          const currentCenterY = (tempPosition[1] + tempPosition[3]) / 2;
          let newTop = currentCenterY - height / 2;
          let newBottom = currentCenterY + height / 2;

          // 边界处理
          if (newTop < maxImageRange.current[1]) {
            newTop = maxImageRange.current[1];
            newBottom = newTop + height;
            if (newBottom > maxImageRange.current[3]) {
              newBottom = maxImageRange.current[3];
              height = newBottom - newTop;
              width = height * aspect;
              newLeft = tempPosition[2] - width;
            }
          } else if (newBottom > maxImageRange.current[3]) {
            newBottom = maxImageRange.current[3];
            newTop = newBottom - height;
            if (newTop < maxImageRange.current[1]) {
              newTop = maxImageRange.current[1];
              height = newBottom - newTop;
              width = height * aspect;
              newLeft = tempPosition[2] - width;
            }
          }

          tempPosition[0] = newLeft;
          tempPosition[1] = newTop;
          tempPosition[3] = newBottom;
        } else {
          tempPosition[0] = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
        }
        break;
      case 'right':
        if (aspect) {
          // 固定比例，右边跟随鼠标，固定左边，上下两边同时增加
          let newRight = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
          let width = newRight - tempPosition[0];
          let height = width / aspect;

          // 计算上下居中的位置
          const currentCenterY = (tempPosition[1] + tempPosition[3]) / 2;
          let newTop = currentCenterY - height / 2;
          let newBottom = currentCenterY + height / 2;

          // 边界处理
          if (newTop < maxImageRange.current[1]) {
            newTop = maxImageRange.current[1];
            newBottom = newTop + height;
            if (newBottom > maxImageRange.current[3]) {
              newBottom = maxImageRange.current[3];
              height = newBottom - newTop;
              width = height * aspect;
              newRight = tempPosition[0] + width;
            }
          } else if (newBottom > maxImageRange.current[3]) {
            newBottom = maxImageRange.current[3];
            newTop = newBottom - height;
            if (newTop < maxImageRange.current[1]) {
              newTop = maxImageRange.current[1];
              height = newBottom - newTop;
              width = height * aspect;
              newRight = tempPosition[0] + width;
            }
          }

          tempPosition[2] = newRight;
          tempPosition[1] = newTop;
          tempPosition[3] = newBottom;
        } else {
          tempPosition[2] = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
        }
        break;
      case 'top':
        if (aspect) {
          // 固定比例，上边跟随鼠标，固定下边，左右两边同时增加
          let newTop = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
          let height = tempPosition[3] - newTop;
          let width = height * aspect;

          // 计算左右居中的位置
          const currentCenterX = (tempPosition[0] + tempPosition[2]) / 2;
          let newLeft = currentCenterX - width / 2;
          let newRight = currentCenterX + width / 2;

          // 边界处理
          if (newLeft < maxImageRange.current[0]) {
            newLeft = maxImageRange.current[0];
            newRight = newLeft + width;
            if (newRight > maxImageRange.current[2]) {
              newRight = maxImageRange.current[2];
              width = newRight - newLeft;
              height = width / aspect;
              newTop = tempPosition[3] - height;
            }
          } else if (newRight > maxImageRange.current[2]) {
            newRight = maxImageRange.current[2];
            newLeft = newRight - width;
            if (newLeft < maxImageRange.current[0]) {
              newLeft = maxImageRange.current[0];
              width = newRight - newLeft;
              height = width / aspect;
              newTop = tempPosition[3] - height;
            }
          }

          tempPosition[1] = newTop;
          tempPosition[0] = newLeft;
          tempPosition[2] = newRight;
        } else {
          tempPosition[1] = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
        }
        break;
      case 'bottom':
        if (aspect) {
          // 固定比例，下边跟随鼠标，固定上边，左右两边同时增加
          let newBottom = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
          let height = newBottom - tempPosition[1];
          let width = height * aspect;

          // 计算左右居中的位置
          const currentCenterX = (tempPosition[0] + tempPosition[2]) / 2;
          let newLeft = currentCenterX - width / 2;
          let newRight = currentCenterX + width / 2;

          // 边界处理
          if (newLeft < maxImageRange.current[0]) {
            newLeft = maxImageRange.current[0];
            newRight = newLeft + width;
            if (newRight > maxImageRange.current[2]) {
              newRight = maxImageRange.current[2];
              width = newRight - newLeft;
              height = width / aspect;
              newBottom = tempPosition[1] + height;
            }
          } else if (newRight > maxImageRange.current[2]) {
            newRight = maxImageRange.current[2];
            newLeft = newRight - width;
            if (newLeft < maxImageRange.current[0]) {
              newLeft = maxImageRange.current[0];
              width = newRight - newLeft;
              height = width / aspect;
              newBottom = tempPosition[1] + height;
            }
          }

          tempPosition[3] = newBottom;
          tempPosition[0] = newLeft;
          tempPosition[2] = newRight;
        } else {
          tempPosition[3] = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
        }
        break;
      case 'topLeft':
        if (aspect) {
          // 固定比例，左上角跟随鼠标，右下角固定
          let newLeft = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
          let newTop = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
          // 以右下角为锚点，根据鼠标位置计算最优缩放
          const widthFromLeft = tempPosition[2] - newLeft;
          const heightFromTop = tempPosition[3] - newTop;
          // 优先跟随鼠标移动距离更大的方向
          if (Math.abs(offsetX - moveStart[1]) > Math.abs(offsetY - moveStart[2])) {
            // 水平移动距离更大，以宽度为准
            let height = widthFromLeft / aspect;
            newTop = tempPosition[3] - height;
            if (newTop < maxImageRange.current[1]) {
              newTop = maxImageRange.current[1];
              height = tempPosition[3] - newTop;
              const width = height * aspect;
              newLeft = tempPosition[2] - width;
            }
          } else {
            // 垂直移动距离更大，以高度为准
            let width = heightFromTop * aspect;
            newLeft = tempPosition[2] - width;
            if (newLeft < maxImageRange.current[0]) {
              newLeft = maxImageRange.current[0];
              width = tempPosition[2] - newLeft;
              const height = width / aspect;
              newTop = tempPosition[3] - height;
            }
          }
          tempPosition[0] = newLeft;
          tempPosition[1] = newTop;
        } else {
          tempPosition[0] = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
          tempPosition[1] = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
        }
        break;
      case 'topRight':
        if (aspect) {
          // 固定比例，右上角跟随鼠标，左下角固定
          let newRight = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
          let newTop = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
          // 优先跟随鼠标移动距离更大的方向
          if (Math.abs(offsetX - moveStart[1]) > Math.abs(offsetY - moveStart[2])) {
            // 水平移动距离更大，以宽度为准
            let width = newRight - tempPosition[0];
            let height = width / aspect;
            newTop = tempPosition[3] - height;
            if (newTop < maxImageRange.current[1]) {
              newTop = maxImageRange.current[1];
              height = tempPosition[3] - newTop;
              width = height * aspect;
              newRight = tempPosition[0] + width;
            }
          } else {
            // 垂直移动距离更大，以高度为准
            let height = tempPosition[3] - newTop;
            let width = height * aspect;
            newRight = tempPosition[0] + width;
            if (newRight > maxImageRange.current[2]) {
              newRight = maxImageRange.current[2];
              width = newRight - tempPosition[0];
              height = width / aspect;
              newTop = tempPosition[3] - height;
            }
          }
          tempPosition[2] = newRight;
          tempPosition[1] = newTop;
        } else {
          tempPosition[2] = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
          tempPosition[1] = Math.max(tempPosition[1] + dy, maxImageRange.current[1]);
        }
        break;
      case 'bottomLeft':
        if (aspect) {
          // 固定比例，左下角跟随鼠标，右上角固定
          let newLeft = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
          let newBottom = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
          // 优先跟随鼠标移动距离更大的方向
          if (Math.abs(offsetX - moveStart[1]) > Math.abs(offsetY - moveStart[2])) {
            // 水平移动距离更大，以宽度为准
            let width = tempPosition[2] - newLeft;
            let height = width / aspect;
            newBottom = tempPosition[1] + height;
            if (newBottom > maxImageRange.current[3]) {
              newBottom = maxImageRange.current[3];
              height = newBottom - tempPosition[1];
              width = height * aspect;
              newLeft = tempPosition[2] - width;
            }
          } else {
            // 垂直移动距离更大，以高度为准
            let height = newBottom - tempPosition[1];
            let width = height * aspect;
            newLeft = tempPosition[2] - width;
            if (newLeft < maxImageRange.current[0]) {
              newLeft = maxImageRange.current[0];
              width = tempPosition[2] - newLeft;
              height = width / aspect;
              newBottom = tempPosition[1] + height;
            }
          }
          tempPosition[0] = newLeft;
          tempPosition[3] = newBottom;
        } else {
          tempPosition[0] = Math.max(tempPosition[0] + dx, maxImageRange.current[0]);
          tempPosition[3] = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
        }
        break;
      case 'bottomRight':
        if (aspect) {
          // 固定比例，右下角跟随鼠标，左上角固定
          // 直接使用鼠标位置作为目标点，然后根据比例约束调整
          const newRight = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
          const newBottom = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
          // 根据当前的宽和高，计算哪个方向受限更少
          const width = newRight - tempPosition[0];
          const height = newBottom - tempPosition[1];
          const heightFromWidth = width / aspect;
          const widthFromHeight = height * aspect;

          // 如果基于宽度计算的高度不超出边界，优先使用宽度
          if (tempPosition[1] + heightFromWidth <= maxImageRange.current[3]) {
            tempPosition[2] = newRight;
            tempPosition[3] = tempPosition[1] + heightFromWidth;
          }
          // 否则基于高度计算宽度
          else if (tempPosition[0] + widthFromHeight <= maxImageRange.current[2]) {
            tempPosition[2] = tempPosition[0] + widthFromHeight;
            tempPosition[3] = newBottom;
          }
          // 如果都超出，使用边界限制
          else {
            const maxWidth = maxImageRange.current[2] - tempPosition[0];
            const maxHeight = maxImageRange.current[3] - tempPosition[1];
            if (maxWidth / aspect <= maxHeight) {
              tempPosition[2] = tempPosition[0] + maxWidth;
              tempPosition[3] = tempPosition[1] + maxWidth / aspect;
            } else {
              tempPosition[2] = tempPosition[0] + maxHeight * aspect;
              tempPosition[3] = tempPosition[1] + maxHeight;
            }
          }
        } else {
          tempPosition[2] = Math.min(tempPosition[2] + dx, maxImageRange.current[2]);
          tempPosition[3] = Math.min(tempPosition[3] + dy, maxImageRange.current[3]);
        }
        break;
      case 'center': {
        // 整体平移
        const [left, top, right, bottom] = tempPosition;
        if (
          left + dx < maxImageRange.current[0] ||
          right + dx > maxImageRange.current[2] ||
          top + dy < maxImageRange.current[1] ||
          bottom + dy > maxImageRange.current[3]
        ) {
          const width = right - left;
          const height = bottom - top;
          if (left + dx < maxImageRange.current[0]) {
            tempPosition[0] = maxImageRange.current[0];
            tempPosition[2] = maxImageRange.current[0] + width;
          } else if (right + dx > maxImageRange.current[2]) {
            tempPosition[2] = maxImageRange.current[2];
            tempPosition[0] = maxImageRange.current[2] - width;
          } else {
            tempPosition[0] += dx;
            tempPosition[2] += dx;
          }
          if (top + dy < maxImageRange.current[1]) {
            tempPosition[1] = maxImageRange.current[1];
            tempPosition[3] = maxImageRange.current[1] + height;
          } else if (bottom + dy > maxImageRange.current[3]) {
            tempPosition[3] = maxImageRange.current[3];
            tempPosition[1] = maxImageRange.current[3] - height;
          } else {
            tempPosition[1] += dy;
            tempPosition[3] += dy;
          }
        } else {
          tempPosition[0] += dx;
          tempPosition[1] += dy;
          tempPosition[2] += dx;
          tempPosition[3] += dy;
        }
        break;
      }
    }

    if (isValidPosition(tempPosition, imageScale, MAX_IMAGE_RATIO)) {
      setRange(tempPosition);
      moveStart[1] = offsetX;
      moveStart[2] = offsetY;
    }
  };

  // 剪裁蒙层
  const left = rangeLeft;
  const top = rangeTop;
  const right = rangeRight;
  const bottom = rangeBottom;
  const clipMaskDom = (
    <svg className="absolute inset-0 size-full overflow-visible p-4">
      <defs>
        <mask id="mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect x={left} y={top} width={right - left} height={bottom - top} fill="black" />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 0, 0, 0.4)" mask="url(#mask)" />

      {/* 虚线边框 */}
      <line id="line-left" x1={left} y1={top} x2={left} y2={bottom} {...lineStyle} />
      <line id="line-top" x1={left} y1={top} x2={right} y2={top} {...lineStyle} />
      <line id="line-right" x1={right} y1={top} x2={right} y2={bottom} {...lineStyle} />
      <line id="line-bottom" x1={left} y1={bottom} x2={right} y2={bottom} {...lineStyle} />

      {/* 边的拖动区域 */}
      <rect
        cursor="col-resize"
        x={left - DETECT_RANGE / 2}
        y={top + DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={bottom - top - DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['left', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="row-resize"
        x={left + DETECT_RANGE / 2}
        y={top - DETECT_RANGE / 2}
        width={right - left - DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['top', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="col-resize"
        x={right - DETECT_RANGE / 2}
        y={top + DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={bottom - top - DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['right', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="row-resize"
        x={left + DETECT_RANGE / 2}
        y={bottom - DETECT_RANGE / 2}
        width={right - left - DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['bottom', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />

      {/* 中心拖动区域 */}
      <rect
        cursor="all-scroll"
        x={left + DETECT_RANGE / 2}
        y={top + DETECT_RANGE / 2}
        width={right - left - DETECT_RANGE}
        height={bottom - top - DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['center', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />

      {/* 角落拖动区域 - 最后渲染，确保在最上层 */}
      <rect
        cursor="nwse-resize"
        x={left - DETECT_RANGE / 2}
        y={top - DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['topLeft', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="nesw-resize"
        x={right - DETECT_RANGE / 2}
        y={top - DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['topRight', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="nwse-resize"
        x={right - DETECT_RANGE / 2}
        y={bottom - DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['bottomRight', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />
      <rect
        cursor="nesw-resize"
        x={left - DETECT_RANGE / 2}
        y={bottom - DETECT_RANGE / 2}
        width={DETECT_RANGE}
        height={DETECT_RANGE}
        onPointerDown={(event) => setMoveStart(['bottomLeft', event.nativeEvent.offsetX, event.nativeEvent.offsetY])}
        fill="transparent"
      />

      {/* 井字格辅助线 */}
      {/* 垂直三等分线 */}
      <line
        x1={left + (right - left) / 3}
        y1={top}
        x2={left + (right - left) / 3}
        y2={bottom}
        stroke="rgba(255, 255, 255, 0.3)"
        strokeWidth="1"
        pointerEvents="none"
      />
      <line
        x1={left + ((right - left) * 2) / 3}
        y1={top}
        x2={left + ((right - left) * 2) / 3}
        y2={bottom}
        stroke="rgba(255, 255, 255, 0.3)"
        strokeWidth="1"
        pointerEvents="none"
      />
      {/* 水平三等分线 */}
      <line
        x1={left}
        y1={top + (bottom - top) / 3}
        x2={right}
        y2={top + (bottom - top) / 3}
        stroke="rgba(255, 255, 255, 0.3)"
        strokeWidth="1"
        pointerEvents="none"
      />
      <line
        x1={left}
        y1={top + ((bottom - top) * 2) / 3}
        x2={right}
        y2={top + ((bottom - top) * 2) / 3}
        stroke="rgba(255, 255, 255, 0.3)"
        strokeWidth="1"
        pointerEvents="none"
      />

      {/* 白色标记线 */}
      {/* 左侧中点白色实线 */}
      <line
        x1={left}
        y1={(top + bottom) / 2 - 12}
        x2={left}
        y2={(top + bottom) / 2 + 12}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      {/* 左上角白色实线 */}
      <line x1={left - 2.5} y1={top} x2={left + 12} y2={top} stroke="white" strokeWidth="5" pointerEvents="none" />
      <line x1={left} y1={top - 2.5} x2={left} y2={top + 12} stroke="white" strokeWidth="5" pointerEvents="none" />

      {/* 顶部中点白色实线 */}
      <line
        x1={(left + right) / 2 - 12}
        y1={top}
        x2={(left + right) / 2 + 12}
        y2={top}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      {/* 右上角白色实线 */}
      <line x1={right - 12} y1={top} x2={right + 2.5} y2={top} stroke="white" strokeWidth="5" pointerEvents="none" />
      <line x1={right} y1={top - 2.5} x2={right} y2={top + 12} stroke="white" strokeWidth="5" pointerEvents="none" />

      {/* 右侧中点白色实线 */}
      <line
        x1={right}
        y1={(top + bottom) / 2 - 12}
        x2={right}
        y2={(top + bottom) / 2 + 12}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      {/* 右下角白色实线 */}
      <line
        x1={right - 12}
        y1={bottom}
        x2={right + 2.5}
        y2={bottom}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      <line
        x1={right}
        y1={bottom - 12}
        x2={right}
        y2={bottom + 2.5}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />

      {/* 底部中点白色实线 */}
      <line
        x1={(left + right) / 2 - 12}
        y1={bottom}
        x2={(left + right) / 2 + 12}
        y2={bottom}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      {/* 左下角白色实线 */}
      <line
        x1={left - 2.5}
        y1={bottom}
        x2={left + 12}
        y2={bottom}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
      <line
        x1={left}
        y1={bottom - 12}
        x2={left}
        y2={bottom + 2.5}
        stroke="white"
        strokeWidth="5"
        pointerEvents="none"
      />
    </svg>
  );

  return {
    clipMaskDom,
    handleMoving,
    handleMoveEnd: () => setMoveStart(null),
  };
}
